import { useEffect, useReducer, useState } from 'react';
import { getStreamAuthToken } from '../../../auth/streamAuth';
import { API_BASE_URL } from '../../../api/axios';
import { databaseMigrationsApi } from '../../../api/databaseMigrations';
import {
  initialState,
  progressReducer,
  type ProgressEvent,
  type ProgressState,
} from './migrationProgressReducer';

export type ProgressSource = 'idle' | 'sse' | 'polling';

interface Options {
  pollIntervalMs?: number;
  sseWatchdogMs?: number;
  pollingCeilingMs?: number;
}

const DEFAULT_OPTS: Required<Options> = {
  pollIntervalMs: 2000,
  sseWatchdogMs: 4000,
  pollingCeilingMs: 5 * 60 * 1000,
};

export interface UseMigrationProgressResult extends ProgressState {
  source: ProgressSource;
  ceilingReached: boolean;
  acknowledgeCeiling: () => void;
}

/**
 * Drives RunProgressPanel. Opens SSE first; if no event arrives within
 * `sseWatchdogMs`, or the stream errors/404s, falls back to polling
 * GET /migrations/runs/:runId every `pollIntervalMs`. Either source feeds
 * the same pure reducer (see migrationProgressReducer.ts) so the UI does
 * not branch on transport.
 *
 * `targets` entries are database names (`RunTarget.targetDb`): tenant runs
 * pass database names, the registry run passes `['cma_registry']` (the
 * server-side alias `'__registry__'` is normalized to it). SSE events and
 * poll rows are re-keyed against the passed targets via `normalizeKey`, so
 * server keys (companyId, `'__registry__'`) all resolve to the UI's keys —
 * otherwise `allTerminal` would never satisfy and polling would run until
 * the 5-minute ceiling.
 */
export function useMigrationProgress(
  runId: string | null,
  targets: string[],
  opts: Options = {},
): UseMigrationProgressResult {
  const { pollIntervalMs, sseWatchdogMs, pollingCeilingMs } = { ...DEFAULT_OPTS, ...opts };

  const [state, dispatch] = useReducer(progressReducer, targets, initialState);
  const [source, setSource] = useState<ProgressSource>('idle');
  const [ceilingReached, setCeilingReached] = useState(false);

  // Note: state is reset by the consumer passing `runId` as React `key` on
  // <RunProgressPanel>. A new runId remounts the component which re-runs
  // the reducer with fresh initialState. No internal reset effect required.

  useEffect(() => {
    if (!runId) return;

    const ctrl = new AbortController();
    let sseGotFirstEvent = false;
    let pollingStarted = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let ceilingTimer: ReturnType<typeof setTimeout> | null = null;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    let prevSnapshotStatuses: Record<string, string> = Object.fromEntries(targets.map((t) => [t, 'pending']));
    let stopped = false;

    const stopAll = () => {
      stopped = true;
      try { ctrl.abort(); } catch {}
      if (pollTimer) clearTimeout(pollTimer);
      if (ceilingTimer) clearTimeout(ceilingTimer);
      if (watchdog) clearTimeout(watchdog);
    };

    // The server keys tenant targets by companyId and the registry by
    // '__registry__', while the UI passes database names. Resolve every
    // incoming key to whichever form appears in `targets`.
    const targetSet = new Set(targets);
    const REGISTRY_ALIASES: Record<string, string> = {
      __registry__: 'cma_registry',
      cma_registry: '__registry__',
    };
    const normalizeKey = (...candidates: Array<string | number | null | undefined>): string => {
      for (const c of candidates) {
        if (c == null || c === '') continue;
        const s = String(c);
        if (targetSet.has(s)) return s;
        const alias = REGISTRY_ALIASES[s];
        if (alias && targetSet.has(alias)) return alias;
      }
      const first = candidates.find((c) => c != null && c !== '');
      return first != null ? String(first) : '';
    };

    const dispatchEvent = (evt: ProgressEvent) => {
      if (stopped) return;
      if ('target' in evt) {
        dispatch({ ...evt, target: normalizeKey(evt.targetDb, evt.target) });
      } else {
        dispatch(evt);
      }
    };

    const armCeiling = () => {
      if (ceilingTimer) clearTimeout(ceilingTimer);
      ceilingTimer = setTimeout(() => setCeilingReached(true), pollingCeilingMs);
    };

    const startPolling = () => {
      if (stopped || pollingStarted) return;
      pollingStarted = true;
      setSource('polling');
      armCeiling();
      const tick = async () => {
        if (stopped) return;
        try {
          const detail = await databaseMigrationsApi.getRun(runId);
          // Diff in target-input order.
          const nextStatuses: Record<string, string> = {};
          for (const t of detail.targets) {
            const key = normalizeKey(t.targetDb, t.companyId);
            nextStatuses[key] = t.status;
            const prev = prevSnapshotStatuses[key] ?? 'pending';
            const cur = t.status;
            if (prev === cur) continue;
            if (prev === 'pending' && cur === 'running') {
              dispatchEvent({ type: 'target-start', target: key });
            }
            if (cur === 'success') {
              if (prev === 'pending') dispatchEvent({ type: 'target-start', target: key });
              dispatchEvent({ type: 'target-success', target: key });
            }
            if (cur === 'failed') {
              if (prev === 'pending') dispatchEvent({ type: 'target-start', target: key });
              dispatchEvent({ type: 'target-failed', target: key, error: t.errorMessage ?? 'Failed' });
            }
          }
          prevSnapshotStatuses = nextStatuses;
          const allTerminal = targets.every((t) => {
            const s = nextStatuses[t];
            return s === 'success' || s === 'failed' || s === 'rolled_back';
          });
          if (allTerminal) {
            const anyFailed = Object.values(nextStatuses).some((s) => s === 'failed');
            dispatchEvent({ type: 'run-complete', status: anyFailed ? 'failed' : 'success' });
            stopAll();
            return;
          }
        } catch (err) {
          // 404 from polling AND prior SSE failure → run truly unknown; we surface via UI.
          // For 5xx, retry with simple backoff up to 3 times then give up silently.
          // (Operator can refresh history table.)
          // Implementation simplification: don't escalate — just continue ticking.
        }
        if (!stopped) pollTimer = setTimeout(tick, pollIntervalMs);
      };
      tick();
    };

    const trySse = async () => {
      setSource('sse');
      // Better Auth first, OIDC only as a fallback — an empty token 401s and
      // the caller falls back to polling, which looks like a stalled run.
      const token = await getStreamAuthToken();

      let res: Response;
      try {
        res = await fetch(`${API_BASE_URL}/super-admin/database/migrations/run/${runId}/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: ctrl.signal,
          credentials: 'include',
        });
      } catch {
        startPolling();
        return;
      }

      if (!res.ok || !res.body) {
        startPolling();
        return;
      }

      // SSE watchdog: if no event within sseWatchdogMs, fall back.
      watchdog = setTimeout(() => {
        if (sseGotFirstEvent) return;
        try { ctrl.abort(); } catch {}
        startPolling();
      }, sseWatchdogMs);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';
          for (const block of events) {
            const dataLine = block.split('\n').find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const evt = JSON.parse(dataLine.slice(6)) as ProgressEvent;
              sseGotFirstEvent = true;
              dispatchEvent(evt);
              if (evt.type === 'run-complete') {
                stopAll();
                return;
              }
            } catch {
              /* malformed event, skip */
            }
          }
        }
        // Stream ended without run-complete → fall back.
        if (!stopped) startPolling();
      } catch {
        if (!stopped) startPolling();
      }
    };

    trySse();

    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const acknowledgeCeiling = () => {
    // Dismiss the "still polling?" banner. Polling continues silently in the
    // background. We do NOT re-arm the timer — re-showing the banner would
    // be more noise than signal once the operator has acknowledged.
    setCeilingReached(false);
  };

  return { ...state, source, ceilingReached, acknowledgeCeiling };
}
